import { useCallback, useEffect, useReducer, useRef } from 'react'
import type { CallStartResponse, CallTransport } from 'api/admin/coach/types'
import type { CallState } from '../context/VoiceAgentCallContext'
import { useVapiWebSocket } from './useVapiWebSocket'
import { useMicrophone } from './useMicrophone'
import { useAudioPlayback } from './useAudioPlayback'
import { useVoiceTranscript } from './useVoiceTranscript'

// ---------------------------------------------------------------------------
// Reducer — single source of truth for call lifecycle state
// ---------------------------------------------------------------------------

type SessionState = {
  callState: CallState
  callId: string | null
  websocketUrl: string | null
  error: string | null
}

type SessionAction =
  | { type: 'CONNECT' }
  | { type: 'CALL_CREATED'; callId: string; websocketUrl: string }
  | { type: 'ACTIVE' }
  | { type: 'ENDED' }
  | { type: 'ERROR'; error: string }
  | { type: 'RESET' }

function sessionReducer(
  state: SessionState,
  action: SessionAction
): SessionState {
  switch (action.type) {
    case 'CONNECT':
      return { ...state, callState: 'connecting', error: null }
    case 'CALL_CREATED':
      return {
        ...state,
        callId: action.callId,
        websocketUrl: action.websocketUrl,
      }
    case 'ACTIVE':
      return { ...state, callState: 'active' }
    case 'ENDED':
      if (state.callState === 'idle' || state.callState === 'ended') {
        return state
      }
      return { ...state, callState: 'ended', websocketUrl: null }
    case 'ERROR':
      return {
        ...state,
        callState: 'error',
        error: action.error,
        websocketUrl: null,
      }
    case 'RESET':
      return {
        callState: 'idle',
        callId: null,
        websocketUrl: null,
        error: null,
      }
    default:
      return state
  }
}

type StartCallRequest = {
  transport: CallTransport
}

type StartCall = (request: StartCallRequest) => Promise<CallStartResponse>

type UseVoiceCallSessionOptions = {
  startCall: StartCall
}

export const useVoiceCallSession = ({
  startCall: startCallRequest,
}: UseVoiceCallSessionOptions) => {
  const [session, dispatch] = useReducer(sessionReducer, {
    callState: 'idle',
    callId: null,
    websocketUrl: null,
    error: null,
  })

  const {
    transcript,
    processEntry,
    clear: clearTranscript,
  } = useVoiceTranscript()
  const { playChunk, stop: stopPlayback } = useAudioPlayback()

  // Refs so WebSocket callbacks can call imperative teardown methods
  // without stale closures.
  const micRef = useRef<{ stop: () => void } | null>(null)
  const stopPlaybackRef = useRef<() => void>(() => undefined)

  // --- WebSocket callbacks ------------------------------------------------

  const handleControlMessage = useCallback(
    (message: { type: string; [key: string]: unknown }) => {
      if (message.type === 'transcript') {
        processEntry(
          message.role as 'user' | 'assistant',
          message.transcript as string,
          message.transcriptType === 'final'
        )
      }

      if (message.type === 'status-update' && message.status === 'ended') {
        micRef.current?.stop()
        stopPlaybackRef.current()
        dispatch({ type: 'ENDED' })
      }
    },
    [processEntry]
  )

  const onAudioData = useCallback(
    (data: Int16Array) => playChunk(data),
    [playChunk]
  )

  const { connectionState, send, disconnect } = useVapiWebSocket({
    url: session.websocketUrl,
    onAudioData,
    onControlMessage: handleControlMessage,
    onClose: () => {
      micRef.current?.stop()
      stopPlaybackRef.current()
      dispatch({ type: 'ENDED' })
    },
    onError: () => {
      dispatch({ type: 'ERROR', error: 'WebSocket connection error' })
    },
  })

  // --- Microphone ---------------------------------------------------------

  const onMicChunk = useCallback((chunk: ArrayBuffer) => send(chunk), [send])

  // Promote to 'active' once the WebSocket connects
  const mic = useMicrophone({ onAudioChunk: onMicChunk })
  micRef.current = mic
  stopPlaybackRef.current = stopPlayback

  const prevConnectionState = useRef(connectionState)
  useEffect(() => {
    if (
      prevConnectionState.current === 'connecting' &&
      connectionState === 'connected'
    ) {
      dispatch({ type: 'ACTIVE' })
      mic.start()
    }
    prevConnectionState.current = connectionState
  }, [connectionState, mic.start])

  // Tear down the call if the mic fails
  useEffect(() => {
    if (mic.error) {
      disconnect()
      stopPlayback()
      dispatch({ type: 'ERROR', error: mic.error })
    }
  }, [mic.error, disconnect, stopPlayback])

  // --- Public actions -----------------------------------------------------

  const startCall = useCallback(() => {
    clearTranscript()
    dispatch({ type: 'CONNECT' })

    void startCallRequest({
      transport: {
        provider: 'vapi.websocket',
        audioFormat: {
          format: 'pcm_s16le',
          container: 'raw',
          sampleRate: 16000,
        },
      },
    })
      .then((data) => {
        dispatch({
          type: 'CALL_CREATED',
          callId: data.callId,
          websocketUrl: data.websocketUrl,
        })
      })
      .catch((err) => {
        dispatch({
          type: 'ERROR',
          error: err instanceof Error ? err.message : 'Failed to start call',
        })
      })
  }, [clearTranscript, startCallRequest])

  const endCall = useCallback(() => {
    mic.stop()
    disconnect()
    stopPlayback()
    dispatch({ type: 'ENDED' })
  }, [mic.stop, disconnect, stopPlayback])

  const reset = useCallback(() => {
    clearTranscript()
    dispatch({ type: 'RESET' })
  }, [clearTranscript])

  return {
    callState: session.callState,
    callId: session.callId,
    error: session.error,
    isMuted: mic.isMuted,
    transcript,
    startCall,
    endCall,
    reset,
    toggleMute: mic.toggleMute,
  }
}
